import { TableChildRow, TableRow } from "@carbon6/c6-frontend-design";
import type { TrainingBudgetUnit } from "@/api";
import { groupPoliciesByDataset } from "./evalPolicyGroups";
import type { EvaluationTargetPolicy } from "./evaluationTargetPolicy";

// 평가 대상 policy 표의 행 모델. 최상위 행은 학습 dataset 그룹이다. 그 dataset 으로 학습한
// policy 는 그룹 행이 가진 하위 행(TableChildRow)이다. 그래서 c6 가 그룹 행 바로 아래에 그린다.
// 그룹 순서는 groupPoliciesByDataset 가 소유한다. 여기서는 그 결과를 행으로 감싸기만 한다.

// 그룹 행. dataset 표시명 하나를 가진다. c6 DataTableColumn.key 는 행 프로퍼티로 제한되므로 네
// 컬럼의 키를 여기서 선언한다. 그룹 행은 policy 값(id·epoch·createdAtMs)이 없으므로 null 로 두고
// 셀을 비운다. 값은 하위 행(EvalPolicyRow)이 가진다.
export class EvalPolicyGroupRow extends TableRow {
  // "Policy" 열. 그룹 행은 dataset 표시명이다(registry 표시명, "training-infra 전용 데이터셋", "—" 중 하나).
  readonly label: string;
  readonly policyCount: number;
  readonly policies: readonly EvalPolicyRow[];
  readonly id = null;
  readonly epoch = null;
  readonly createdAtMs = null;

  constructor(label: string, policies: readonly EvaluationTargetPolicy[]) {
    super(null);
    this.label = label;
    this.policyCount = policies.length;
    const rowId = this.getRowId();
    this.policies = policies.map((policy) => new EvalPolicyRow(rowId, policy));
  }

  // groupPoliciesByDataset 의 그룹 키가 label 이라 유일하다. 하위 행(policy:)과 접두사로 id 공간을
  // 구분한다.
  getRowId(): string {
    return `dataset:${this.label}`;
  }

  canShow(): boolean {
    return true;
  }

  // 이 표에는 선택 UI 가 없다.
  canSelect(): boolean {
    return false;
  }

  // 선언은 펼침과 무관하다(c6 PIT-011). 펼침 값은 정하지 않는다(isRowChildrenExpanded undefined).
  // 그러면 표의 defaultRowChildrenExpanded 가 값을 채운다. 접기 UI 가 없는 표라 항상 펼친다.
  override getRowChildren(): readonly EvalPolicyRow[] {
    return this.policies;
  }
}

// 하위 행. policy 한 건이다. 표 계약 없이 식별과 가시성만 맡는다. 내용 렌더는 EvalPolicyTable 의
// renderRowChild 가 맡는다.
export class EvalPolicyRow extends TableChildRow {
  readonly id: string;
  readonly name: string;
  readonly epoch: number | null;
  readonly budgetUnit: TrainingBudgetUnit;
  readonly createdAtMs: number | null;

  constructor(parentRowId: string, policy: EvaluationTargetPolicy) {
    super(parentRowId);
    this.id = policy.id;
    this.name = policy.name;
    this.epoch = policy.epoch;
    this.budgetUnit = policy.budgetUnit;
    this.createdAtMs = policy.createdAtMs;
  }

  getRowId(): string {
    return `policy:${this.id}`;
  }

  canShow(): boolean {
    return true;
  }

  canSelect(): boolean {
    return false;
  }
}

export function createEvalPolicyTableRows(
  policies: readonly EvaluationTargetPolicy[],
): readonly EvalPolicyGroupRow[] {
  return groupPoliciesByDataset(policies).map(
    (group) => new EvalPolicyGroupRow(group.label, group.policies),
  );
}
